import {
  Icon,
  IconButton,
  type IconButtonProps,
  type IconProps,
} from '@chakra-ui/react'
import { type ConditionalValue } from '@chakra-ui/react'
import React, { forwardRef, useMemo, useRef } from 'react'
import { Tooltip } from '~/components/ui/tooltip'
import { Size } from '~/types/ThemeTypes'
import { getNodeText } from '~/utils/objectUtils'

import {
  getQuickActionButtonStyles as mergeQuickActionButtonStyles,
  QuickActionButtonVariants,
} from './componentStyles/QuickActionButton.style'

const shallowEqual = <T extends object>(
  a: T | undefined,
  b: T | undefined,
): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  const keysA = Object.keys(a) as (keyof T)[]
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every((key) => Object.is(a[key], b[key]))
}

export type QuickActionButtonSizes =
  Size.xs | Size.sm | Size.md | Size.lg | Size.xl

interface QuickActionButtonProps extends Omit<
  IconButtonProps,
  'aria-label' | 'variant'
> {
  variant: QuickActionButtonVariants
  size?: ConditionalValue<QuickActionButtonSizes>
  actionIcon: React.ComponentType<any>
  tooltipText: string | React.ReactNode
  /** Accessible name; defaults to the text content of `tooltipText`. */
  ariaLabel?: string
  tooltipDisabled?: boolean
  iconProps?: IconProps
  isActive?: boolean
}
export const QuickActionButton = forwardRef(function QuickActionButton(
  {
    variant,
    size,
    actionIcon,
    tooltipText,
    ariaLabel,
    tooltipDisabled,
    iconProps,
    disabled,
    isActive,
    onMouseEnter,
    onMouseLeave,
    ...rest
  }: QuickActionButtonProps,
  ref: React.Ref<HTMLButtonElement> | undefined,
) {
  const [isHovered, setIsHovered] = React.useState(false)

  const tooltipAriaLabel = useMemo(() => {
    return ariaLabel || getNodeText(tooltipText)
  }, [ariaLabel, tooltipText])

  const stylesMemoKey = `${variant}-${size}`
  const styles = useMemo(
    () => mergeQuickActionButtonStyles({ variant, size }),
    [stylesMemoKey],
  )

  const iconButtonProps = useMemo(
    () => ({
      display: styles.iconButton?.display,
      alignItems: styles.iconButton?.alignItems,
      justifyContent: styles.iconButton?.justifyContent,
    }),
    [styles],
  )

  const iconPropsRef = useRef(iconProps)
  if (!shallowEqual(iconPropsRef.current, iconProps)) {
    iconPropsRef.current = iconProps
  }

  const iconStyles = useMemo(
    () => ({ ...styles.icon, ...iconPropsRef.current }),
    [styles, iconPropsRef.current],
  )

  const ActionIconComp = actionIcon

  return (
    <Tooltip
      open={isHovered && tooltipDisabled !== true}
      showArrow
      positioning={{ placement: 'top' }}
      content={tooltipText}
      disabled={disabled}
    >
      <IconButton
        size={size}
        data-group
        data-active={isActive ? 'true' : undefined}
        ref={ref}
        css={styles.iconButton}
        {...iconButtonProps}
        aria-label={tooltipAriaLabel}
        disabled={disabled}
        {...rest}
        className={`group ${rest.className || ''}`}
        onMouseEnter={(e) => {
          setIsHovered(true)
          if (onMouseEnter) onMouseEnter(e)
        }}
        onMouseLeave={(e) => {
          setIsHovered(false)
          if (onMouseLeave) onMouseLeave(e)
        }}
      >
        <Icon css={iconStyles}>
          <ActionIconComp size="100%" />
        </Icon>
      </IconButton>
    </Tooltip>
  )
})
